/**
 * Run Intelligence Engine
 *
 * Analyzes logged run data to provide coaching intelligence that
 * surpasses what a human coach can do — because it sees every run,
 * every pace, every pattern, in real time.
 *
 * Features:
 * - Adaptive pace recommendations from actual performance
 * - Race readiness score (adherence + fitness + consistency)
 * - Overtraining / injury risk detection
 * - Contextual coaching messages
 * - Effort trend analysis
 */

import { getCurrentWeek, getThisWeekRuns, getWeekCompletionMap } from "./run-plan";

// ─── Pace Analysis ───

/**
 * Compute average pace (min/mi) for a given run type from recent logs.
 * Uses exponential weighting so recent runs matter more.
 */
export function getAveragePace(runs, runType, windowDays = 28) {
  const cutoff = Date.now() - windowDays * 86400000;
  const matching = (runs || [])
    .filter((r) => r.timestamp >= cutoff && r.runType === runType && r.pace > 0)
    .sort((a, b) => a.timestamp - b.timestamp);

  if (!matching.length) return null;

  let weightedSum = 0;
  let weightTotal = 0;
  matching.forEach((r, i) => {
    const weight = Math.pow(1.3, i); // More recent = higher weight
    weightedSum += r.pace * weight;
    weightTotal += weight;
  });

  return Math.round((weightedSum / weightTotal) * 100) / 100;
}

/**
 * Get adaptive pace guidance for a scheduled run type,
 * based on the runner's actual logged paces.
 */
export function getAdaptivePace(runs, scheduledRunType) {
  const easyPace = getAveragePace(runs, "Easy");
  const tempoPace = getAveragePace(runs, "Tempo");
  const longPace = getAveragePace(runs, "Long Run");
  const intervalPace = getAveragePace(runs, "Intervals");

  // If we don't have enough data, return null (use plan's default guidance)
  const basePace = easyPace || tempoPace || longPace;
  if (!basePace) return null;

  const fmt = (pace) => {
    if (!pace) return null;
    const min = Math.floor(pace);
    const sec = Math.round((pace - min) * 60);
    return `${min}:${sec.toString().padStart(2, "0")}`;
  };

  switch (scheduledRunType) {
    case "Easy":
    case "Recovery": {
      const pace = easyPace || (tempoPace ? tempoPace + 1.5 : null);
      if (!pace) return null;
      // Easy should be comfortable — suggest a range
      const slow = fmt(pace + 0.3);
      const fast = fmt(pace - 0.2);
      return { pace, display: `${fast}–${slow}/mi`, label: "Based on your recent easy runs" };
    }
    case "Tempo": {
      const pace = tempoPace || (easyPace ? easyPace - 1.0 : null);
      if (!pace) return null;
      const target = fmt(pace);
      return { pace, display: `~${target}/mi`, label: "Based on your recent tempo pace" };
    }
    case "Long Run": {
      const pace = longPace || easyPace || (tempoPace ? tempoPace + 1.0 : null);
      if (!pace) return null;
      const slow = fmt(pace + 0.3);
      const fast = fmt(pace - 0.15);
      return { pace, display: `${fast}–${slow}/mi`, label: "Keep it conversational" };
    }
    case "Intervals": {
      const pace = intervalPace || (tempoPace ? tempoPace - 0.5 : null) || (easyPace ? easyPace - 1.5 : null);
      if (!pace) return null;
      const target = fmt(pace);
      return { pace, display: `~${target}/mi for intervals`, label: "Based on your recent speed work" };
    }
    case "Race Pace": {
      const pace = tempoPace || (easyPace ? easyPace - 1.2 : null);
      if (!pace) return null;
      const target = fmt(pace);
      return { pace, display: `~${target}/mi`, label: "Your projected race pace" };
    }
    default:
      return null;
  }
}

// ─── Race Readiness Score ───

/**
 * Compute a 0-100 race readiness score based on:
 * - Plan adherence (40%): % of scheduled runs completed
 * - Mileage consistency (25%): hitting weekly targets
 * - Pace fitness (20%): are paces improving or stable?
 * - Consistency streak (15%): how many weeks in a row had 80%+ adherence?
 */
export function getRaceReadiness(plan, runs) {
  if (!plan?.plan?.weeks || !runs?.length) return null;

  const currentWeek = getCurrentWeek(plan);
  const totalWeeks = plan.plan.total_weeks || 1;

  // 1. Plan adherence — % of total runs completed across all past weeks
  let totalScheduled = 0;
  let totalCompleted = 0;
  const weekAdherenceHistory = [];

  for (let w = 1; w <= currentWeek; w++) {
    const weekData = plan.plan.weeks.find((wd) => wd.week_number === w);
    if (!weekData?.runs) continue;
    const scheduled = weekData.runs.filter((r) => r.type !== "Rest" && r.type !== "Cross Train");
    totalScheduled += scheduled.length;

    // For current week, use smart matching; for past weeks, count by date range
    if (w === currentWeek) {
      const map = getWeekCompletionMap(plan, runs);
      const weekCompleted = Object.values(map).filter((v) => v.completed).length;
      totalCompleted += weekCompleted;
      weekAdherenceHistory.push(scheduled.length > 0 ? weekCompleted / scheduled.length : 1);
    } else {
      // Past weeks: count runs logged within that week's date range
      const planStart = new Date(plan.generated_at);
      planStart.setHours(0, 0, 0, 0);
      const startMonday = new Date(planStart);
      startMonday.setDate(startMonday.getDate() - ((startMonday.getDay() + 6) % 7));
      const weekMonday = new Date(startMonday);
      weekMonday.setDate(weekMonday.getDate() + (w - 1) * 7);
      const weekSunday = new Date(weekMonday);
      weekSunday.setDate(weekSunday.getDate() + 6);
      weekSunday.setHours(23, 59, 59, 999);

      const weekRuns = runs.filter((r) => {
        const d = new Date(r.timestamp);
        return d >= weekMonday && d <= weekSunday;
      });
      const completed = Math.min(weekRuns.length, scheduled.length);
      totalCompleted += completed;
      weekAdherenceHistory.push(scheduled.length > 0 ? completed / scheduled.length : 1);
    }
  }

  const adherenceScore = totalScheduled > 0 ? (totalCompleted / totalScheduled) * 100 : 0;

  // 2. Mileage consistency — average of (actual / planned) per week, capped at 100%
  let mileageScoreSum = 0;
  let mileageWeeks = 0;
  for (let w = 1; w <= currentWeek; w++) {
    const weekData = plan.plan.weeks.find((wd) => wd.week_number === w);
    const planned = weekData?.total_mileage || 0;
    if (planned <= 0) continue;

    const planStart = new Date(plan.generated_at);
    planStart.setHours(0, 0, 0, 0);
    const startMonday = new Date(planStart);
    startMonday.setDate(startMonday.getDate() - ((startMonday.getDay() + 6) % 7));
    const weekMonday = new Date(startMonday);
    weekMonday.setDate(weekMonday.getDate() + (w - 1) * 7);
    const weekSunday = new Date(weekMonday);
    weekSunday.setDate(weekSunday.getDate() + 6);
    weekSunday.setHours(23, 59, 59, 999);

    const actual = runs
      .filter((r) => {
        const d = new Date(r.timestamp);
        return d >= weekMonday && d <= weekSunday;
      })
      .reduce((sum, r) => sum + (r.unit === "km" ? r.distance * 0.621371 : r.distance), 0);

    mileageScoreSum += Math.min(1, actual / planned);
    mileageWeeks++;
  }
  const mileageScore = mileageWeeks > 0 ? (mileageScoreSum / mileageWeeks) * 100 : 0;

  // 3. Pace fitness — are recent paces stable or improving?
  // Compare last 2 weeks of easy pace vs previous 2 weeks
  const recentPace = getAveragePace(runs, "Easy", 14);
  const olderPace = getAveragePace(
    (runs || []).filter((r) => {
      const age = Date.now() - r.timestamp;
      return age >= 14 * 86400000 && age < 28 * 86400000;
    }),
    "Easy",
    28
  );

  let paceScore = 50; // Default: neutral
  if (recentPace && olderPace) {
    const improvement = olderPace - recentPace; // Positive = faster (better)
    if (improvement > 0.3) paceScore = 100;
    else if (improvement > 0.1) paceScore = 80;
    else if (improvement > -0.1) paceScore = 60; // Stable
    else if (improvement > -0.3) paceScore = 35;
    else paceScore = 15; // Getting slower — possible fatigue
  } else if (recentPace) {
    paceScore = 55; // Have data but no comparison
  }

  // 4. Consistency streak — count consecutive weeks with 80%+ adherence
  let streak = 0;
  for (let i = weekAdherenceHistory.length - 1; i >= 0; i--) {
    if (weekAdherenceHistory[i] >= 0.8) streak++;
    else break;
  }
  const maxStreak = currentWeek;
  const consistencyScore = maxStreak > 0 ? Math.min(100, (streak / Math.min(maxStreak, 4)) * 100) : 0;

  // Weighted total
  const overall = Math.round(
    adherenceScore * 0.4 +
    mileageScore * 0.25 +
    paceScore * 0.2 +
    consistencyScore * 0.15
  );

  return {
    overall: Math.min(100, Math.max(0, overall)),
    adherence: Math.round(adherenceScore),
    mileage: Math.round(mileageScore),
    paceFitness: Math.round(paceScore),
    consistency: Math.round(consistencyScore),
    consistencyStreak: streak,
    weekProgress: `${currentWeek}/${totalWeeks}`,
    label: overall >= 85 ? "Race ready" :
           overall >= 70 ? "On track" :
           overall >= 50 ? "Building" :
           overall >= 30 ? "Getting started" : "Needs attention",
  };
}

// ─── Overtraining / Injury Risk ───

/**
 * Detect potential overtraining signals:
 * - Sudden pace drops (>15% slower than recent average)
 * - Running significantly more than planned
 * - No rest days in the past 5 days
 * - Rapid mileage increase week-over-week (>15%)
 */
export function getInjuryRisk(plan, runs) {
  if (!runs?.length || runs.length < 3) return { level: "none", signals: [] };

  const signals = [];
  const recent = [...runs].sort((a, b) => b.timestamp - a.timestamp);

  // 1. Pace drop detection — last run vs 4-week average
  const lastRun = recent[0];
  const avgPace = getAveragePace(runs, lastRun.runType, 28);
  if (avgPace && lastRun.pace > 0) {
    const dropPct = ((lastRun.pace - avgPace) / avgPace) * 100;
    if (dropPct > 15) {
      signals.push({
        type: "pace_drop",
        severity: dropPct > 25 ? "high" : "medium",
        message: `Your last ${lastRun.runType.toLowerCase()} was ${Math.round(dropPct)}% slower than usual. This could mean fatigue — consider an easy day.`,
      });
    }
  }

  // 2. No rest days — check if user ran 5+ consecutive days
  const recentDates = new Set();
  for (const r of recent.slice(0, 10)) {
    const d = new Date(r.timestamp);
    recentDates.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
  }
  // Count consecutive days backward from today
  let consecutiveDays = 0;
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (recentDates.has(key)) consecutiveDays++;
    else break;
  }
  if (consecutiveDays >= 5) {
    signals.push({
      type: "no_rest",
      severity: consecutiveDays >= 7 ? "high" : "medium",
      message: `You've run ${consecutiveDays} days straight. Your muscles need recovery to get stronger. Take a rest day.`,
    });
  }

  // 3. Mileage spike — this week vs last week
  if (plan) {
    const thisWeekMiles = getWeekLoggedMiles(runs, 0);
    const lastWeekMiles = getWeekLoggedMiles(runs, 1);
    if (lastWeekMiles > 5 && thisWeekMiles > 0) {
      const increase = ((thisWeekMiles - lastWeekMiles) / lastWeekMiles) * 100;
      if (increase > 15) {
        signals.push({
          type: "mileage_spike",
          severity: increase > 30 ? "high" : "medium",
          message: `Mileage is up ${Math.round(increase)}% from last week. The safe guideline is ≤10% increase. Listen to your body.`,
        });
      }
    }
  }

  const level = signals.some((s) => s.severity === "high")
    ? "high"
    : signals.length > 0
      ? "medium"
      : "none";

  return { level, signals };
}

/** Helper: get total logged miles for a given week offset (0 = this week, 1 = last week) */
function getWeekLoggedMiles(runs, weekOffset) {
  const now = new Date();
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7) - weekOffset * 7);
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  return (runs || [])
    .filter((r) => {
      const d = new Date(r.timestamp);
      return d >= monday && d <= sunday;
    })
    .reduce((sum, r) => sum + (r.unit === "km" ? r.distance * 0.621371 : r.distance), 0);
}

// ─── Contextual Coaching Messages ───

/**
 * Generate a smart, context-aware coaching message for the home card.
 * Considers: yesterday's run, this week's progress, upcoming run, risk signals.
 */
export function getSmartCoachMessage(plan, runs) {
  if (!plan) return null;

  const recent = [...(runs || [])].sort((a, b) => b.timestamp - a.timestamp);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = `${yesterday.getFullYear()}-${yesterday.getMonth()}-${yesterday.getDate()}`;

  const yesterdayRun = recent.find((r) => {
    const d = new Date(r.timestamp);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` === yesterdayStr;
  });

  const risk = getInjuryRisk(plan, runs);
  const completionMap = getWeekCompletionMap(plan, runs);
  const completedCount = Object.values(completionMap).filter((v) => v.completed).length;
  const weekRuns = getThisWeekRuns(plan);
  const scheduledCount = weekRuns.filter((r) => r.type !== "Rest" && r.type !== "Cross Train").length;

  // Priority: injury risk > post-hard-effort recovery > weekly momentum > general
  if (risk.level === "high") {
    return { message: risk.signals[0].message, type: "warning" };
  }

  if (yesterdayRun) {
    if (yesterdayRun.runType === "Long Run") {
      return {
        message: "Good long run yesterday. Today focus on recovery — light stretching, hydration, and easy movement.",
        type: "recovery",
      };
    }
    if (yesterdayRun.runType === "Intervals" || yesterdayRun.runType === "Tempo") {
      return {
        message: `Solid ${yesterdayRun.runType.toLowerCase()} session yesterday. Your legs earned some easy effort today.`,
        type: "recovery",
      };
    }
  }

  if (risk.level === "medium") {
    return { message: risk.signals[0].message, type: "caution" };
  }

  if (completedCount >= scheduledCount && scheduledCount > 0) {
    return {
      message: "All scheduled runs done this week. Great execution — you're building real fitness.",
      type: "celebration",
    };
  }

  if (completedCount > 0 && completedCount >= scheduledCount * 0.5) {
    return {
      message: `${completedCount} of ${scheduledCount} runs this week — solid momentum. Keep showing up.`,
      type: "momentum",
    };
  }

  return null;
}

// ─── Pace Trend for display ───

/**
 * Get pace trend data for the last N weeks, grouped by run type.
 * Returns array of { week, easyPace, tempoPace, longPace } for charting.
 */
export function getPaceTrend(runs, weeks = 6) {
  const trend = [];
  for (let w = weeks - 1; w >= 0; w--) {
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - (w + 1) * 7);
    const weekEnd = new Date();
    weekEnd.setDate(weekEnd.getDate() - w * 7);

    const weekRuns = (runs || []).filter((r) => {
      const d = new Date(r.timestamp);
      return d >= weekStart && d < weekEnd;
    });

    const avgForType = (type) => {
      const matching = weekRuns.filter((r) => r.runType === type && r.pace > 0);
      if (!matching.length) return null;
      return Math.round((matching.reduce((s, r) => s + r.pace, 0) / matching.length) * 100) / 100;
    };

    trend.push({
      weekAgo: w,
      label: w === 0 ? "This wk" : `${w}wk ago`,
      easyPace: avgForType("Easy"),
      tempoPace: avgForType("Tempo"),
      longPace: avgForType("Long Run"),
    });
  }
  return trend;
}
